import { useState } from "react"

import { Box, Drawer, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"

import { Button, IconButton, SvgIcon, type FC } from ".."
import SearchForm from "./SearchForm"

type Facet = {
  label: string
  value: string
}

type TripSearchFormProps = {
  destinations?: Facet[]
  countries?: Facet[]
  experiences?: Facet[]
  durations?: Facet[]
  prices?: Facet[]
  onSearch?: () => void
  onApplyFilter?: (value: any, type: string) => void
  searchParams?: any
}

const TripSearchForm: FC<TripSearchFormProps> = ({
  destinations = [],
  countries = [],
  experiences = [],
  prices = [],
  durations = [],
  onSearch,
  onApplyFilter,
  searchParams,
}) => {
  const [openDialog, setOpenDialog] = useState(false)

  return (
    <>
      <Box display={{ xs: "none", md: "block" }}>
        <SearchForm
          destinations={destinations}
          countries={countries}
          experiences={experiences}
          prices={prices}
          durations={durations}
          onSearch={onSearch as () => void}
          onApplyFilter={onApplyFilter as any}
          searchParams={searchParams}
        />
      </Box>
      <Box display={{ md: "none" }}>
        <Button
          variant="base"
          onClick={() => setOpenDialog(true)}
          sx={(theme) => ({
            border: `1px solid ${theme.palette["neutral.200"].main}`,
            bgcolor: theme.palette["neutral.100"].main,
            borderRadius: 1.5,
            justifyContent: "flex-start",
            width: { xs: "100%" },
            height: { xs: 54 },
            px: { xs: 5 },
          })}
          startIcon={<SvgIcon variant="sort" size={16} />}
          endIcon={<SvgIcon variant="bullet" size={8} sx={{ transform: "rotate(90deg)" }} />}
        >
          <Typography variant="link.sm" ml={3} mr="auto">
            Filter By
          </Typography>
        </Button>
      </Box>
      <SearchDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        ariaLabel="Filter Trip Itineraries"
        destinations={destinations}
        countries={countries}
        experiences={experiences}
        onSearch={onSearch as () => void}
        onApplyFilter={onApplyFilter as any}
      />
    </>
  )
}
export default TripSearchForm

type DrawerProps = {
  open: boolean
  onClose: () => void
  ariaLabel?: string
  destinations?: Facet[]
  countries?: Facet[]
  experiences?: Facet[]
  prices?: Facet[]
  durations?: Facet[]
  onSearch?: (test: any) => void
  onApplyFilter?: (value: any, type: string) => void
}

const SearchDialog: FC<DrawerProps> = ({
  open,
  onClose,
  ariaLabel,
  destinations,
  countries,
  experiences,
  prices,
  durations,
  onApplyFilter,
}) => {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      aria-label={ariaLabel}
      anchor="bottom"
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: (theme) => alpha(theme.palette["neutral.900"].main, 0.4),
          },
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          flexDirection: "row",
          alignItems: "stretch",
          flexWrap: "wrap",
          display: { md: "grid" },
          gridTemplateColumns: { md: "clamp(350px, 35.15625%, 450px) 1fr" },
          borderRadius: (theme) => theme.spacing(1.5, 1.5, 0, 0),
          mx: 3,
          p: 5,
          minHeight: 728,
          zIndex: 4100,
        },
      }}
      sx={{
        zIndex: 4000,
      }}
    >
      <Stack flex="1 1 100%">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4.5}>
          <Typography variant="link.xs" sx={{ opacity: 0.7 }}>
            {ariaLabel}
          </Typography>
          <IconButton
            variant="secondary"
            onClick={onClose}
            icon={<SvgIcon variant="x" size={14} />}
            sx={{
              width: { xs: 44 },
              height: { xs: 44 },
            }}
          />
        </Stack>
        <Box flex="1 1 100%">
          <SearchForm
            destinations={destinations}
            countries={countries}
            experiences={experiences}
            prices={prices}
            durations={durations}
            onSearch={onClose}
            onApplyFilter={onApplyFilter as any}
          />
        </Box>
      </Stack>
    </Drawer>
  )
}
